import React, { SyntheticEvent } from 'react'
import {
  EditableComponent,
  ModelProps
} from '@adobe/aem-react-editable-components'
import { Accordion as UIFoundationsAccordion } from '@dcxwam/dcx-wam-ui-foundations'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import Container from '../../templates/Container/Container'
import AccordionEditConfig from './AccordionEditConfig'
import { pushDataLayer } from '../../../utils/datalayer'
import { AccordionModelProps, CqItemModel } from './types'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  WithAutomaticTestIdsType,
  withAutomaticTestIds
} from '../../../utils/aem/with-automatic-test-ids'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'

const Accordion = withComponentRefresh(
  withAutomaticTestIds(
    (
      props: WithAutomaticTestIdsType<
        EditableComponentPropsType<AccordionModelProps>
      >
    ) => {
      const { cqItems, id, ...rest } = props.model
      const { getDataTestIdObject } = props
      const editableProps = useExtractEditableProps(props, rest)
      const accordionConfig = {
        items: (Object.values(cqItems ?? {}) as CqItemModel[]).map((item) => {
          const accordionContent = {
            model: {
              ...item,
              cqItems: item[':items'],
              cqItemsOrder: item[':itemsOrder'],
              cqType: item[':type']
            }
          }
          return {
            titleProps: {
              title: item?.title || '',
              imageProps: {
                src: item?.imagePath,
                height: item?.imageHeight,
                width: item?.imageWidth,
                imageAlt: item?.imgAlt,
                imageSrcSetData: item?.imageSrcSet
              }
            },
            ...(item?.gtmItem && item.gtmItem.length > 0
              ? {
                  onToggle: (_event: SyntheticEvent, expanded: boolean) => {
                    if (expanded && item?.gtmItem) {
                      pushDataLayer(item.gtmItem)
                    }
                  }
                }
              : {}),
            children: (
              <Container model={{ ...accordionContent } as ModelProps} />
            )
          }
        }),
        ...getDataTestIdObject('accordion', id)
      }

      return (
        <EditableComponent {...editableProps}>
          {AccordionEditConfig.isEmpty(props)
            ? AccordionEditConfig.emptyLabel
            : null}
          {!AccordionEditConfig.isEmpty(props) &&
            (AuthoringUtils.isInEditor() ? (
              <Container {...props} />
            ) : (
              <UIFoundationsAccordion {...accordionConfig} />
            ))}
        </EditableComponent>
      )
    }
  )
)

const AccordionWithErrorBoundary = withErrorBoundary(Accordion, {
  componentName: 'Accordion',
  fallback: <ErrorFallback componentName="Accordion" />
})

export default AccordionWithErrorBoundary
